import React, { useState, useEffect, useRef, useId } from 'react';
import { signUp, signIn, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword, fetchAuthSession } from 'aws-amplify/auth';
import { color, gradient, shadow, alpha } from '../config/tokens';

const AuthModal = ({ isOpen, onClose, onAuthenticated }) => {
  const [mode, setMode] = useState('signin'); // 'signin', 'signup', 'confirm', 'forgotPassword', 'resetPassword'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const titleId = useId();
  const emailId = useId();
  const passwordId = useId();
  const displayNameId = useId();
  const confirmCodeId = useId();

  // Focus trap and escape key handling
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn({ username: email, password });
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      onAuthenticated({
        email,
        displayName: payload?.name || email.split('@')[0],
        idToken: session.tokens?.idToken?.toString(),
        sub: payload?.sub
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Sign in failed');
    }
    setLoading(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name: displayName || email.split('@')[0],
            'custom:marketing_consent': marketingConsent ? 'true' : 'false'
          }
        }
      });
      setMode('confirm');
    } catch (err) {
      setError(err.message || 'Sign up failed');
    }
    setLoading(false);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: confirmCode });
      // Auto sign in after confirmation
      await signIn({ username: email, password });
      const session = await fetchAuthSession();
      onAuthenticated({
        email,
        displayName: displayName || email.split('@')[0],
        idToken: session.tokens?.idToken?.toString(),
        sub: session.tokens?.idToken?.payload?.sub,
        marketingConsent
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      await resendSignUpCode({ username: email });
      setSuccessMessage('A new code is on its way — check your email.');
    } catch (err) {
      setError(err.message || 'Could not resend the code. Please try again.');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setSuccessMessage('Reset code sent! Check your email.');
      setMode('resetPassword');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    }
    setLoading(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: resetCode,
        newPassword
      });
      setSuccessMessage('Password reset successful! You can now sign in.');
      setResetCode('');
      setNewPassword('');
      setMode('signin');
    } catch (err) {
      setError(err.message || 'Password reset failed');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={modalRef}
      tabIndex={-1}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div style={{
        backgroundColor: '#1a1a2e',
        borderRadius: '16px',
        padding: '32px',
        width: '400px',
        maxWidth: '90vw',
        border: `1px solid ${alpha(color.accent.primary, 0.3)}`,
        boxShadow: shadow.lg
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 id={titleId} style={{ margin: 0, color: '#fff', fontSize: '1.5rem' }}>
            {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : mode === 'forgotPassword' || mode === 'resetPassword' ? 'Reset Password' : 'Verify Email'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            style={{
              background: 'none',
              border: 'none',
              color: color.accent.light,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
          >
            &times;
          </button>
        </div>

        {successMessage && <p role="status" style={{ color: color.status.success, marginBottom: '16px' }}>{successMessage}</p>}

        {mode === 'confirm' ? (
          <form onSubmit={handleConfirm}>
            <p style={{ color: '#a0a0a0', marginBottom: '16px' }}>
              We sent a verification code to {email}
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor={confirmCodeId} className="sr-only">Verification Code</label>
              <input
                id={confirmCodeId}
                type="text"
                placeholder="Verification Code"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                style={inputStyle}
                required
                aria-required="true"
                autoComplete="one-time-code"
              />
            </div>
            {error && <p role="alert" style={{ color: color.status.danger, marginBottom: '16px' }}>{error}</p>}
            <button type="submit" style={buttonStyle} disabled={loading} aria-busy={loading}>
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                style={{ background: 'none', border: 'none', color: '#a0a0a0', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                Didn't get a code? Resend
              </button>
            </p>
          </form>
        ) : mode === 'forgotPassword' ? (
          <form onSubmit={handleForgotPassword}>
            <p style={{ color: '#a0a0a0', marginBottom: '16px' }}>
              Enter your email to receive a reset code
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor={emailId} className="sr-only">Email</label>
              <input
                id={emailId}
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
                required
                aria-required="true"
                autoComplete="email"
              />
            </div>
            {error && <p role="alert" style={{ color: color.status.danger, marginBottom: '16px' }}>{error}</p>}
            <button type="submit" style={buttonStyle} disabled={loading} aria-busy={loading}>
              {loading ? 'Sending...' : 'Send Code'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => { setMode('signin'); setError(''); setSuccessMessage(''); }}
                style={{ background: 'none', border: 'none', color: color.accent.primary, cursor: 'pointer' }}
              >
                Back to sign in
              </button>
            </p>
          </form>
        ) : mode === 'resetPassword' ? (
          <form onSubmit={handleConfirmReset}>
            <p style={{ color: '#a0a0a0', marginBottom: '16px' }}>
              Enter the code sent to {email}
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor={confirmCodeId} className="sr-only">Reset Code</label>
              <input
                id={confirmCodeId}
                type="text"
                placeholder="Reset Code"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                style={inputStyle}
                required
                aria-required="true"
                autoComplete="one-time-code"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor={passwordId} className="sr-only">New Password</label>
              <input
                id={passwordId}
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={inputStyle}
                required
                aria-required="true"
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            {error && <p role="alert" style={{ color: color.status.danger, marginBottom: '16px' }}>{error}</p>}
            <button type="submit" style={buttonStyle} disabled={loading} aria-busy={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => { setMode('signin'); setError(''); setSuccessMessage(''); }}
                style={{ background: 'none', border: 'none', color: color.accent.primary, cursor: 'pointer' }}
              >
                Back to sign in
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={mode === 'signin' ? handleSignIn : handleSignUp}>
            {mode === 'signup' && (
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor={displayNameId} className="sr-only">Display Name (optional)</label>
                <input
                  id={displayNameId}
                  type="text"
                  placeholder="Display Name (optional)"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  style={inputStyle}
                  autoComplete="name"
                />
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor={emailId} className="sr-only">Email</label>
              <input
                id={emailId}
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
                required
                aria-required="true"
                autoComplete="email"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor={passwordId} className="sr-only">Password</label>
              <input
                id={passwordId}
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
                required
                aria-required="true"
                minLength={8}
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              />
            </div>
            {mode === 'signup' && (
              <label style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px', color: '#a0a0a0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  style={{ marginRight: '8px', marginTop: '4px' }}
                />
                <span style={{ fontSize: '0.85rem' }}>
                  I'd like to receive updates about Thinking Council and Bee Line Microsystems research projects
                </span>
              </label>
            )}
            {error && <p role="alert" style={{ color: color.status.danger, marginBottom: '16px' }}>{error}</p>}
            <button type="submit" style={buttonStyle} disabled={loading} aria-busy={loading}>
              {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
            </button>
            {mode === 'signin' && (
              <p style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => { setMode('forgotPassword'); setError(''); setSuccessMessage(''); }}
                  style={{ background: 'none', border: 'none', color: '#a0a0a0', cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  Forgot password?
                </button>
              </p>
            )}
          </form>
        )}

        {(mode === 'signin' || mode === 'signup') && (
          <p style={{ textAlign: 'center', marginTop: '16px', color: '#a0a0a0' }}>
            {mode === 'signin' ? (
              <>
                Don't have an account?{' '}
                <button
                  onClick={() => { setMode('signup'); setError(''); setSuccessMessage(''); }}
                  style={{ background: 'none', border: 'none', color: color.accent.primary, cursor: 'pointer' }}
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => { setMode('signin'); setError(''); setSuccessMessage(''); }}
                  style={{ background: 'none', border: 'none', color: color.accent.primary, cursor: 'pointer' }}
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '8px',
  border: `1px solid ${alpha(color.accent.primary, 0.3)}`,
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  color: '#fff',
  fontSize: '1rem',
  boxSizing: 'border-box'
};

const buttonStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: '8px',
  border: 'none',
  background: gradient.primary,
  color: '#fff',
  fontSize: '1rem',
  fontWeight: '600',
  cursor: 'pointer'
};

export default AuthModal;
